/* eslint-disable eqeqeq */
import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom"; 
import api from "./api";

// Components
import LoginPage from "./components/LoginPage";
import Header from "./components/Header"; 
import ProjectDashboardPage from "./components/ProjectDashboardPage";
import UserDashboardPage from "./components/UserDashboardPage";
import VendorDashboardPage from "./components/VendorDashboardPage";
import AuditDashboardPage from "./components/AuditDashboardPage";
import QrStaticPage from "./components/QrStaticPage";
import OverviewPage from "./components/OverviewPage";
import TestSmsPage from "./components/SMSTestPage";
import EmailTestPage from "./components/EmailTestPage";
import UserRegistrationPage from "./components/UserRegistrationPage";
import CreateProjectPage from "./components/CreateProjectPage";
import CreateVoucherPage from "./components/CreateVoucherPage";
import ForgotPasswordPage from "./components/ForgotPasswordPage";
import ResetPasswordPage from "./components/ResetPasswordPage";
import InitiateProjectPage from "./components/InitiateProjectPage";
import BeneficiaryListPage from "./components/UploadBeneficiaryListPage";
import ApproveBeneficiaryListPage from "./components/ApproveBeneficiaryListPage";
import UploadBeneficiaryListPage from "./components/UploadBeneficiaryListPage";
import RegisterCoordinatorPage from "./components/RegisterCoordinatorPage";
import ProjectSelectionPage from "./components/ProjectSelectionPage"; 

import { ToastContainer } from "react-toastify"; 
import "react-toastify/dist/ReactToastify.css";
import "./App.css"; 
import profileIcon from "./assets/profile.png"; 

// ✅ Navigation Config
export const navConfig = {
  ADMIN: { 
    nav: ["overview", "new-coordinator", "initiate-project", "dashboard", "user-dashboard", "audit-dashboard"], 
    defaultRoute: "/overview" 
  },
  "PROJECT COORDINATOR": { nav: ["overview", "user-registration", "create-project", "dashboard"], defaultRoute: "/overview" },
  ISSUER: { nav: ["overview", "create-voucher"], defaultRoute: "/overview" },
  OBSERVER: { nav: ["overview", "dashboard", "user-dashboard"], defaultRoute: "/overview" },
  CHECKER: { nav: ["overview", "approve-beneficiary-list"], defaultRoute: "/overview" },
  MAKER: { nav: ["overview", "beneficiary-list", "approve-beneficiary-list"], defaultRoute: "/overview" },
  VENDOR: { nav: ["vendor-dashboard"], defaultRoute: "/vendor-dashboard" }
};

const AppContent = () => {
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const stored = localStorage.getItem("currentUser");
      if (!stored) return null;
      
      const user = JSON.parse(stored);
      // Restore Nav
      if (user.role) {
          let roleKey = user.role.toUpperCase();
          if (roleKey.startsWith("ROLE_")) roleKey = roleKey.replace("ROLE_", "");
          
          if (navConfig[roleKey]) {
             user.nav = navConfig[roleKey].nav;
          } else if (navConfig[roleKey.replace("_", " ")]) {
             user.nav = navConfig[roleKey.replace("_", " ")].nav;
          }
      }
      return user;
    } catch (e) {
      return null;
    }
  });

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userMenuOpen, setUserMenuOpen] = useState(false); 
  const navigate = useNavigate();
  const location = useLocation(); 

  const handleLogout = useCallback(async () => {
    try { await api.post("/vres/auth/logout"); } catch (error) { console.warn("Logout failed", error); }
    localStorage.clear();
    setCurrentUser(null);
    setUserMenuOpen(false);
    navigate('/login');
  }, [navigate]);

  useEffect(() => {
    const publicPaths = ["/login", "/", "/forgot-password", "/reset-password", "/qr", "/test-sms", "/test-email"];
    if (!publicPaths.includes(location.pathname)) {
       const token = localStorage.getItem("jwtToken") || localStorage.getItem("token");
       if (!token && currentUser) handleLogout(); 
    }
  }, [location, currentUser, handleLogout]);

  useEffect(() => {
    if (!currentUser) return;
    const safePaths = ["/select-project", "/login", "/", "/forgot-password"];
    if (safePaths.some(path => location.pathname.startsWith(path))) return;

    const validRoles = ["ADMIN", "PROJECT COORDINATOR", "MAKER", "CHECKER", "ISSUER", "OBSERVER", "VENDOR"];
    const cleanRole = currentUser.role ? currentUser.role.toUpperCase().replace("ROLE_", "").replace("_", " ") : "";

    if (!currentUser.role || !validRoles.includes(cleanRole)) {
       console.warn(`Security Info: Role '${currentUser.role}' checked against safe list.`);
    }
  }, [location, currentUser]);

  useEffect(() => {
    const token = localStorage.getItem("jwtToken") || localStorage.getItem("token");
    const storedUserStr = localStorage.getItem("currentUser");

    if (token && storedUserStr) {
      let user = JSON.parse(storedUserStr);
      if (user.email === "akhilakandru@gmail.com" || user.id === 1) {
          if (user.role !== "ADMIN") {
             user.role = "ADMIN";
             localStorage.setItem("role", "ADMIN");
          }
      }
      if (user.role) {
          let roleKey = user.role.toUpperCase();
          if (roleKey.startsWith("ROLE_")) roleKey = roleKey.replace("ROLE_", "");
          if (navConfig[roleKey]) user.nav = navConfig[roleKey].nav;
          else if (navConfig[roleKey.replace("_", " ")]) user.nav = navConfig[roleKey.replace("_", " ")].nav;
      }

      if (!currentUser || currentUser.userId !== user.userId || currentUser.selectedProjectId !== user.selectedProjectId || JSON.stringify(currentUser.nav) !== JSON.stringify(user.nav)) {
         setCurrentUser(user);
         localStorage.setItem("currentUser", JSON.stringify(user));
      }
    }
  }, [location, currentUser]); 

  useEffect(() => {
    const closeMenu = () => setUserMenuOpen(false);
    if(userMenuOpen) document.addEventListener('click', closeMenu);
    return () => document.removeEventListener('click', closeMenu);
  }, [userMenuOpen]);

  const getRedirectPath = () => {
      if (!currentUser) return "/login";
      if (currentUser.role === "ADMIN") return "/overview";
      
      // ✅ FIX: Don't force redirect for pages that don't need a project context
      // This allows coordinators to access Create Project without selecting one first.
      const globalPages = ["/create-project", "/user-registration", "/new-coordinator"];
      if (globalPages.includes(location.pathname)) return location.pathname;

      if (!currentUser.selectedProjectId) return "/select-project";
      
      let roleKey = currentUser.role.toUpperCase().replace("ROLE_", "").replace("_", " ");
      return (navConfig[roleKey]?.defaultRoute) || "/overview";
  };

  return (
    <div className="app-wrapper"> 
      {currentUser ? (
        <div className="app-container">
          <Header currentUser={currentUser} sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
          <main className="main-content">
            <header className="top-navbar">
              <div className="nav-title"><h2>Voucher Redemption & Execution System</h2></div>
              <div className="user-profile" onClick={(e) => { e.stopPropagation(); setUserMenuOpen(!userMenuOpen); }}>
                <span style={{ marginRight: '10px' }}>{currentUser.name} <small style={{opacity: 0.7}}>({currentUser.role || 'No Role'})</small></span>
                <img src={profileIcon} alt="Profile" style={{ width: '35px', height: '35px', borderRadius: '50%', border: '2px solid #e1e4e8'}} />
                {userMenuOpen && (
                  <div className="user-dropdown-menu">
                    {currentUser.role !== "ADMIN" && <button className="dropdown-item" onClick={() => navigate('/select-project')}><i className="fa-solid fa-layer-group" style={{marginRight: '8px'}}></i> Switch Project</button>}
                    <button className="dropdown-item" onClick={handleLogout}><i className="fa-solid fa-right-from-bracket" style={{marginRight: '8px'}}></i> Logout</button>
                  </div>
                )}
              </div>
            </header>

            <div className="page-area">
              <Routes>
                {/* ✅ ENSURE ALL ROUTES ARE DEFINED HERE */}
                <Route path="/select-project" element={<ProjectSelectionPage setCurrentUser={setCurrentUser} />} />
                <Route path="/overview" element={<OverviewPage />} />
                <Route path="/dashboard" element={<ProjectDashboardPage currentUser={currentUser} />} />
                <Route path="/user-dashboard" element={<UserDashboardPage />} />
                <Route path="/vendor-dashboard" element={<VendorDashboardPage />} />
                <Route path="/audit-dashboard" element={<AuditDashboardPage />} />
                <Route path="/user-registration" element={<UserRegistrationPage />} />
                
                {/* 👇 THIS WAS LIKELY CAUSING THE REDIRECT LOOP 👇 */}
                <Route path="/create-project" element={<CreateProjectPage />} />
                
                <Route path="/new-coordinator" element={<RegisterCoordinatorPage />} />
                <Route path="/create-voucher" element={<CreateVoucherPage currentUser={currentUser} />} />
                <Route path="/initiate-project" element={<InitiateProjectPage />} />
                <Route path="/beneficiary-list" element={<BeneficiaryListPage readOnly={currentUser?.role === "CHECKER"} />} />
                <Route path="/approve-beneficiary-list" element={<ApproveBeneficiaryListPage role={currentUser?.role ? currentUser.role.toLowerCase() : ""} projectId={currentUser?.selectedProjectId} />} />
                <Route path="/upload-beneficiary-list" element={<UploadBeneficiaryListPage />} />
                <Route path="*" element={<Navigate to={getRedirectPath()} replace />} />
              </Routes>
            </div>
          </main>
        </div>
      ) : (
        <div className="auth-layout">
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/qr" element={<QrStaticPage />} />
            <Route path="/test-sms" element={<TestSmsPage />} />
            <Route path="/test-email" element={<EmailTestPage />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </div>
      )}
      <ToastContainer position="top-center" autoClose={3000} hideProgressBar />
    </div>
  );
};

const App = () => ( <Router><AppContent /></Router> );
export default App;